import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { api } from '../services/api';
import LazyImage from '../components/LazyImage';
import { RotatingLines } from 'react-loader-spinner';
import { Button } from '../components/ui/Button';
import { Input, TextArea } from '../components/ui/Input';
import emailjs from '@emailjs/browser';
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';

const Container = styled.div`
  max-width: ${({ theme }) => theme.breakpoints.large};
  margin: 0 auto;
  padding: 2rem;
`;

const HeaderSection = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin-bottom: 1rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 4rem;
  
  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ContactInfo = styled.div`
  background: #f9f9f9;
  padding: 2rem;
  border-radius: 8px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;
  
  svg {
    color: ${({ theme }) => theme.colors.secondary};
    font-size: 1.5rem;
  }
`;

const Form = styled.form`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.shadows.small};
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Contact = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const formRef = useRef();
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get('/contact')
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    setSending(true);
    
    // Use environment variables for keys
    // Ensure these are set in .env
    const serviceId = process.env.REACT_APP_EMAILJS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
    const publicKey = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

    if (!serviceId || !templateId || !publicKey) {
        console.warn("EmailJS keys are missing");
        setTimeout(() => {
            setSent(true);
            setSending(false);
        }, 1000);
        return;
    }

    emailjs.sendForm(serviceId, templateId, formRef.current, publicKey)
      .then((result) => {
          setSent(true);
      }, (error) => {
          console.log(error.text);
      })
      .finally(() => setSending(false));
  };

  if (loading) return (
    <div style={{display:'flex', justifyContent:'center', marginTop:'50px', minHeight:'100vh'}}>
      <RotatingLines strokeColor="grey" width="50" />
    </div>
  );

  return (
    <Container>
      {data && (
          <HeaderSection>
             {data.image && (
                <div style={{height: '300px', marginBottom: '2rem', borderRadius:'8px', overflow:'hidden'}}>
                    <LazyImage src={data.image} alt={data.title} />
                </div>
             )}
             <Title>{data.title}</Title>
             <p>{data.description}</p>
          </HeaderSection>
      )}

      <Grid>
        <ContactInfo>
            <h3 style={{marginBottom:'2rem'}}>Get In Touch</h3>
            <InfoItem>
                <FaMapMarkerAlt />
                <span>{data?.address || 'Address not available'}</span>
            </InfoItem>
            <InfoItem>
                <FaEnvelope />
                <span>{data?.email || 'Email not available'}</span>
            </InfoItem>
            <InfoItem>
                <FaPhone />
                <span>{data?.phone || 'Phone not available'}</span>
            </InfoItem>
        </ContactInfo>

        <Form ref={formRef} onSubmit={sendEmail}>
            <h3>Send us a Message</h3>
            {sent ? (
                <div style={{color:'green', textAlign:'center'}}>Thank you! Your message has been sent.</div>
            ) : (
                <>
                    <Input type="text" name="user_name" placeholder="Your Name" required />
                    <Input type="email" name="user_email" placeholder="Your Email" required />
                    <TextArea name="message" placeholder="Your Message" required />
                    <Button type="submit" disabled={sending}>
                        {sending ? 'Sending...' : 'Send Message'}
                    </Button>
                </>
            )}
        </Form>
      </Grid>
    </Container>
  );
};

export default Contact;
